// src/components/AddCustomerAddressModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button, MenuItem
} from '@mui/material';
import toast from 'react-hot-toast';
import axios from 'axios';
import { CUSTOMER_ADDRESS_TYPE_OPTIONS, normalizeCustomerAddressType } from '../../../utils/customerAddressTypes';

const COUNTRY_OPTIONS = ['Canada', 'United States', 'Mexico'];

const BLANK = {
  addressName: '',
  addressType: 'both',
  street: '',
  suiteApt: '',
  city: '',
  state: '',
  postal: '',
  country: 'Canada',
};

const getRegionLabel = (country) =>
  country === 'Canada' ? 'Province' : 'State';

const buildInitialValues = (prefill = {}) => {
  const merged = { ...BLANK, ...(prefill || {}) };
  merged.addressType = normalizeCustomerAddressType(prefill?.addressType);
  const hasCountry = String(prefill?.country || '').trim();

  if (!hasCountry) {
    merged.country = 'Canada';
  }

  return merged;
};

export default function AddCustomerAddressModal({
  open,
  onClose,             // () => void
  customerId,          // number | string (required)
  onSaved,             // (savedAddressObj) => void
  prefill,        // optional starter values
}) {
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(null);
  const [values, setValues] = useState(() => buildInitialValues(prefill));

  useEffect(() => {
    if (!open) return;
    setValues(buildInitialValues(prefill));
    requestIdRef.current = null;
  }, [open, prefill]);

  const handleChange = (k) => (e) => {
    requestIdRef.current = null;
    setValues(v => ({ ...v, [k]: e.target.value }));
  };
  const countryOptions = COUNTRY_OPTIONS.includes(values.country)
    ? COUNTRY_OPTIONS
    : [...COUNTRY_OPTIONS, values.country].filter(Boolean);
  const regionLabel = getRegionLabel(values.country);

  const handleSave = async () => {
    if (!customerId) {
      toast.error('Missing customer id');
      return;
    }
    // quick validation
    const required = ['street','city','state','postal','country'];
    const missing = required.filter(k => !String(values[k] || '').trim());
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(', ')}`);
      return;
    }

    try {
      setSaving(true);

      // Create only this address. No cart, proof-preflight, or legacy address rewrite.
      if (!requestIdRef.current) requestIdRef.current = `address-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      const res = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/${customerId}/addresses`,
        { ...values, requestId: requestIdRef.current },
        { headers: { accessToken: localStorage.getItem('accessToken') } }
      );
      const saved = res?.data?.address;
      if (!saved?.id) throw new Error('Address was not returned with an ID');
      toast.success('Address added to customer.');
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      console.warn('add address error', e);
      const errors = e?.response?.data?.errors;
      const message = errors && Object.values(errors).length
        ? Object.values(errors)[0]
        : e?.response?.data?.error || e?.response?.data?.message || 'Failed to add address';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="create-order-dialog-title">Add Customer Address</DialogTitle>
      <DialogContent className="create-order-dialog-content">
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              label="Address Name (optional)"
              fullWidth value={values.addressName}
              onChange={handleChange('addressName')}
              placeholder="e.g., Main Office, Receiving Dock"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Address Type"
              fullWidth
              size="small"
              className="create-order-input"
              value={values.addressType}
              onChange={handleChange('addressType')}
              InputProps={{ sx: { bgcolor: '#fff' } }}
            >
              {CUSTOMER_ADDRESS_TYPE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Street" fullWidth required
              value={values.street} onChange={handleChange('street')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Suite / Unit / Floor (optional)"
              fullWidth
              value={values.suiteApt}
              onChange={handleChange('suiteApt')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="City" fullWidth required
              value={values.city} onChange={handleChange('city')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={regionLabel} fullWidth required
              value={values.state} onChange={handleChange('state')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Postal / ZIP" fullWidth required
              value={values.postal} onChange={handleChange('postal')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Country"
              fullWidth
              required
              value={values.country}
              onChange={handleChange('country')}
            >
              {countryOptions.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save Address'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
